import React, { useMemo } from 'react'

type Props = {
    activeConvoId: string | null
    dynamicVariables: Record<string, string> | null
}

const formatValue = (value: string): string => {
    const trimmed = value.trim()
    if (!trimmed) {
        return '-'
    }
    if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
        try {
            const parsed = JSON.parse(trimmed)
            return JSON.stringify(parsed, null, 2)
        } catch {
            return value
        }
    }
    return value
}

export default function KitDynamicVariablesCard({
    activeConvoId,
    dynamicVariables,
}: Props): React.JSX.Element {
    const entries = useMemo(() => {
        if (!dynamicVariables) {
            return []
        }
        return Object.entries(dynamicVariables).sort(([a], [b]) =>
            a.localeCompare(b),
        )
    }, [dynamicVariables])

    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">
                        Dynamic variables
                    </h2>
                    <p className="text-xs text-muted-foreground">
                        Variables passed into the Kit session.
                    </p>
                </div>
                {activeConvoId ? (
                    <div className="text-xs text-muted-foreground break-all">
                        convId: {activeConvoId}
                    </div>
                ) : null}
            </div>

            <div className="mt-4 space-y-3">
                {entries.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                        Start a Kit conversation to see dynamic variables.
                    </p>
                ) : (
                    entries.map(([key, value]) => (
                        <div
                            key={key}
                            className="rounded-xl border border-border/70 bg-secondary/30 p-3"
                        >
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {key}
                            </p>
                            <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-white/70 p-2 text-[10px] leading-relaxed text-foreground">
                                {formatValue(value)}
                            </pre>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
